import { useCollection } from "@/hooks/useCollection"
import { useDisplayDistance } from "@/hooks/useDisplayDistance"
import {
  createFileRoute,
  Link,
  useNavigate,
  useParams,
} from "@tanstack/react-router"
import { ChevronLeft, Pencil, ShoppingBag, X } from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "@nasti/ui/popover"
import { useAuth } from "@/hooks/useAuth"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@nasti/ui/tabs"
import { CollectionMap } from "@/components/collection/CollectionMap"
import { useState } from "react"
import { Photo } from "@/components/common/Photo"
import { AudioPlayer } from "@/components/common/AudioPlayer"
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch"
import { Button } from "@nasti/ui/button"
import { ROLE } from "@nasti/common/types"
import { Badge } from "@nasti/ui/badge"
import { TaxonName } from "@nasti/common"
import { PhenologyRangeDisplay } from "@nasti/ui/phenologyRangeDisplay"
import { usePersons } from "@/hooks/usePersons"
import { useCollectionContainers, useContainers } from "@/hooks/useContainers"
import { formatDuration } from "@/lib/duration"
import { MATERIAL_TYPE_LABELS, toMaterialTypes } from "@nasti/common/types"

const CollectionDetail = () => {
  const { user, role } = useAuth()
  const { collectionId, id: tripId } = useParams({
    from: "/_private/trips/$id/collections/$collectionId/",
  })

  const collection = useCollection({ collectionId, tripId })
  console.log({ collection })
  const { data: persons } = usePersons(collection?.organisation_id)
  const navigate = useNavigate({
    from: "/trips/$id/collections/$collectionId",
  })

  const handleBackClick = () => {
    navigate({ to: "/trips/$id" })
  }

  const [fullScreenPhoto, setFullScreenPhoto] = useState<string | null>(null)

  const displayDistance = useDisplayDistance(collection?.locationCoord ?? {})

  const canEdit = collection?.created_by === user?.id || role === ROLE.ADMIN
  const presentPeople = persons?.filter((person) =>
    collection?.person_ids?.includes(person.id),
  )
  const formattedDuration = formatDuration(collection?.duration)
  const materialTypes = toMaterialTypes(collection?.material_type)

  const { data: collectionContainers } = useCollectionContainers(collectionId)
  const { data: containers } = useContainers()
  const containerSummary =
    collectionContainers?.map(({ container_id, amount }) => {
      const name =
        containers?.find((container) => container.id === container_id)?.name ??
        "Unknown container"
      return amount === null ? name : `${amount} × ${name}`
    }) ?? []

  if (!collection)
    return (
      <div className="flex h-full w-full flex-col items-start">
        <h2 className="p-2 text-2xl">No collection available</h2>
        <Button
          className="flex w-full justify-start text-lg"
          variant="ghost"
          onClick={handleBackClick}
        >
          <ChevronLeft className="h-5 w-5" /> Back
        </Button>
      </div>
    )

  return (
    <div className="flex flex-col gap-3 px-2">
      <div className="flex items-center justify-between align-middle">
        <div className="flex items-center text-2xl">
          <ChevronLeft onClick={handleBackClick} width={36} height={36} />{" "}
          {collection?.species?.name ? (
            <TaxonName name={collection?.species?.name} />
          ) : (
            collection.field_name
          )}
        </div>
        {canEdit && (
          <Link
            to={`/trips/$id/collections/$collectionId/edit`}
            params={{ id: tripId, collectionId }}
          >
            <Button variant="ghost" size={"icon"}>
              <Pencil className="h-5 w-5" />
            </Button>
          </Link>
        )}
      </div>
      <div className="flex justify-end">
        <Badge className="flex items-center gap-2 rounded text-lg">
          <ShoppingBag className="h-5 w-5" /> Collection
        </Badge>
      </div>
      <table>
        <thead>
          <tr className="text-muted-foreground text-left">
            <th>Time</th>
            <th>Location</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              {collection.created_at && (
                <span>{new Date(collection.created_at).toLocaleString()}</span>
              )}
            </td>
            <td>
              {displayDistance && (
                <Popover>
                  <PopoverTrigger>{displayDistance} km away</PopoverTrigger>
                  <PopoverContent className="w-full">
                    {collection.locationCoord?.latitude.toFixed(6)},{" "}
                    {collection.locationCoord?.longitude.toFixed(6)}
                  </PopoverContent>
                </Popover>
              )}
            </td>
          </tr>
        </tbody>
        <thead>
          <tr className="text-muted-foreground text-left">
            <th>Collector</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              {user?.id === collection.created_by
                ? "You"
                : user?.displayName || "Unknown Person"}
            </td>
          </tr>
        </tbody>
        {presentPeople && presentPeople.length > 0 && (
          <>
            <thead>
              <tr className="text-muted-foreground text-left">
                <th>People present</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="flex flex-wrap gap-2 pt-1">
                  {presentPeople.map((person) => (
                    <Badge
                      key={person.id}
                      variant={person.is_active ? "default" : "secondary"}
                    >
                      {person.display_name}
                    </Badge>
                  ))}
                </td>
              </tr>
            </tbody>
          </>
        )}
      </table>
      {(Boolean(collection.description) ||
        containerSummary.length > 0 ||
        Boolean(formattedDuration) ||
        materialTypes.length > 0 ||
        Boolean(collection.phenology_start)) && (
        <div>
          <hr />
          <table className="w-full table-fixed">
            {formattedDuration && (
              <>
                <thead>
                  <tr className="text-muted-foreground text-left">
                    <th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{formattedDuration}</td>
                  </tr>
                </tbody>
              </>
            )}
            {materialTypes.length > 0 && (
              <>
                <thead>
                  <tr className="text-muted-foreground text-left">
                    <th>Material Collected</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="flex flex-wrap gap-2 pt-1">
                      {materialTypes.map((materialType) => (
                        <Badge key={materialType} variant="secondary">
                          {MATERIAL_TYPE_LABELS[materialType]}
                        </Badge>
                      ))}
                    </td>
                  </tr>
                </tbody>
              </>
            )}
            {containerSummary.length > 0 && (
              <>
                <thead>
                  <tr className="text-muted-foreground text-left">
                    <th>Containers</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{containerSummary.join(", ")}</td>
                  </tr>
                </tbody>
              </>
            )}
            {collection.description && (
              <>
                <thead>
                  <tr className="text-muted-foreground text-left">
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{collection.description}</td>
                  </tr>
                </tbody>
              </>
            )}
            {collection.phenology_start && (
              <>
                <thead>
                  <tr className="text-muted-foreground text-left">
                    <th>Phenology</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="pt-2">
                      <PhenologyRangeDisplay
                        value={[
                          collection.phenology_start,
                          collection.phenology_peak,
                          collection.phenology_end,
                        ]}
                      />
                    </td>
                  </tr>
                </tbody>
              </>
            )}
          </table>
        </div>
      )}
      <Tabs defaultValue="photos">
        <TabsList className="bg-secondary-background mb-2 w-full">
          <TabsTrigger className="w-full" value="photos">
            Photos
          </TabsTrigger>
          <TabsTrigger className="w-full" value="audio">
            Audio
          </TabsTrigger>
          <TabsTrigger className="w-full" value="map">
            Map
          </TabsTrigger>
        </TabsList>
        <TabsContent value="photos" className="grid grid-cols-2 gap-2">
          {collection.photos?.map((photo) => {
            return (
              <Photo
                id={photo.id}
                onClick={setFullScreenPhoto}
                key={photo.id}
                showCaption
                showUploadProgress
              />
            )
          })}
        </TabsContent>
        <TabsContent value="audio" className="flex flex-col gap-2">
          {collection.audios?.map((audio) => (
            <AudioPlayer
              key={audio.id}
              id={audio.id}
              url={audio.url}
              mimeType={audio.mime_type}
              caption={audio.caption}
              durationMs={audio.duration_ms}
              showCaption
              showUploadProgress
            />
          ))}
          {collection.audios?.length === 0 && (
            <span className="text-muted-foreground">No audio recordings.</span>
          )}
        </TabsContent>
        <TabsContent value="map" className="">
          <CollectionMap tripId={tripId} collectionId={collectionId} />
        </TabsContent>
      </Tabs>

      {/* Full-screen overlay */}
      {fullScreenPhoto && (
        <div className="safe-fullscreen-overlay fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90">
          <button
            onClick={() => setFullScreenPhoto(null)}
            className="safe-top-right absolute p-2"
            aria-label="Close photo"
          >
            <X size={28} className="text-white" />
          </button>
          <TransformWrapper>
            <TransformComponent wrapperClass="overflow-visible">
              <img src={fullScreenPhoto} alt="Full screen" />
            </TransformComponent>
          </TransformWrapper>
        </div>
      )}
    </div>
  )
}

export const Route = createFileRoute(
  "/_private/trips/$id/collections/$collectionId/",
)({
  component: CollectionDetail,
})
